import { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { createAdminUser, updateAdminUser } from "../../../../Actions/AdminActions";
import { formatApiError } from "../../../../Actions/Utils";
import {
  getCadreOptions,
  getKbOptionsForCadre,
  getApproachForCadreKb,
} from "../../../../NewConstants/AdminAgentConstants";

const USER_TYPE_OPTIONS = ["Internal", "External - User", "External - Stakeholder"];

const EMPTY_AGENT = {
  cadre: "",
  kb: "",
  approach: "",
  onboarding: false,
  consent: false,
  is_active: true,
};

const EMPTY_FORM = {
  phone: "",
  name: "",
  age: "",
  gender: "",
  state: "",
  district: "",
  sector: "",
  block: "",
  dob: "",
  unique_id: "",
  user_type: "Internal",
};

const CADRE_OPTIONS = getCadreOptions();

function AgentRow({ agent, index, onChange, onRemove, canRemove }) {
  function update(field, value) {
    onChange(index, { ...agent, [field]: value });
  }

  function handleCadreChange(cadre) {
    const kbOptions = getKbOptionsForCadre(cadre);
    const kb = kbOptions.length === 1 ? kbOptions[0] : "";
    const approach = kb ? getApproachForCadreKb(cadre, kb) : "";
    onChange(index, { ...agent, cadre, kb, approach });
  }

  function handleKbChange(kb) {
    const approach = getApproachForCadreKb(agent.cadre, kb);
    onChange(index, { ...agent, kb, approach });
  }

  const kbOptions = getKbOptionsForCadre(agent.cadre);
  const cadreOptions =
    agent.cadre && !CADRE_OPTIONS.includes(agent.cadre)
      ? [...CADRE_OPTIONS, agent.cadre]
      : CADRE_OPTIONS;
  const kbSelectOptions =
    agent.kb && agent.cadre && !kbOptions.includes(agent.kb)
      ? [...kbOptions, agent.kb]
      : kbOptions;
  const configuredApproach = getApproachForCadreKb(agent.cadre, agent.kb);
  const approachSelectOptions = configuredApproach
    ? [configuredApproach]
    : agent.approach
    ? [agent.approach]
    : [];

  return (
    <div className="relative bg-gray-50 border border-gray-200 rounded-xl p-4">
      {canRemove && (
        <button
          type="button"
          onClick={() => onRemove(index)}
          className="absolute top-3 right-3 text-gray-400 hover:text-red-500 transition-colors"
          title="Remove agent"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}

      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
        Agent {index + 1}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Cadre *</label>
          <select
            required
            value={agent.cadre}
            onChange={(e) => handleCadreChange(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
          >
            <option value="">Select cadre</option>
            {cadreOptions.map((cadre) => (
              <option key={cadre} value={cadre}>
                {cadre}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">KB *</label>
          <select
            required
            value={agent.kb}
            onChange={(e) => handleKbChange(e.target.value)}
            disabled={!agent.cadre}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            <option value="">{agent.cadre ? "Select KB" : "Select cadre first"}</option>
            {kbSelectOptions.map((kb) => (
              <option key={kb} value={kb}>
                {kb}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Approach *</label>
          <select
            required
            value={agent.approach}
            disabled={!agent.cadre || !agent.kb}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            <option value="">
              {!agent.cadre ? "Select cadre first" : !agent.kb ? "Select KB first" : "—"}
            </option>
            {approachSelectOptions.map((approach) => (
              <option key={approach} value={approach}>
                {approach}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-5">
        {[
          { field: "onboarding", label: "Onboarding" },
          { field: "consent", label: "Consent" },
          { field: "is_active", label: "Active" },
        ].map(({ field, label }) => (
          <label key={field} className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={agent[field]}
              onChange={(e) => update(field, e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
            />
            <span className="text-sm text-gray-700">{label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function FormField({ label, required, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
    </div>
  );
}

export default function UserForm({ mode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams();

  const existingUser = location.state?.user || null;

  const [form, setForm] = useState(EMPTY_FORM);
  const [agents, setAgents] = useState([{ ...EMPTY_AGENT }]);
  const [version, setVersion] = useState(0);

  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const isEdit = mode === "edit";
  const isInternal = form.user_type === "Internal";

  useEffect(() => {
    if (isEdit && existingUser) {
      const u = existingUser;
      setForm({
        phone: u.phone || "",
        name: u.name || "",
        age: u.age != null ? String(u.age) : "",
        gender: u.gender ? u.gender.toLowerCase() : "",
        state: u.location?.state?.en || u.state || "",
        district: u.location?.district?.en || u.district || "",
        sector: u.location?.sector?.en || u.sector || "",
        block: u.location?.block?.en || u.block || "",
        dob: u.dob ? u.dob.slice(0, 10) : "",
        unique_id: u.unique_id || "",
        user_type: USER_TYPE_OPTIONS.includes(u.user_type)
          ? u.user_type
          : u.user_type === "External"
          ? "External - User"
          : "Internal",
      });
      setVersion(u.version ?? 0);
      if (u.agents && u.agents.length > 0) {
        setAgents(
          u.agents.map((a) => ({
            cadre: a.cadre || "",
            kb: a.kb || "",
            approach: getApproachForCadreKb(a.cadre, a.kb) || a.approach || "",
            onboarding: a.onboarding ?? false,
            consent: a.consent ?? false,
            is_active: a.is_active ?? true,
          }))
        );
      }
    }
  }, [isEdit, existingUser]);

  function updateForm(field, value) {
    if (field === "user_type" && value !== "Internal") {
      setAgents((prev) => prev.slice(0, 1));
    }
    setForm((f) => ({ ...f, [field]: value }));
    setFieldErrors((fe) => ({ ...fe, [field]: "" }));
  }

  function handleAgentChange(index, updated) {
    setAgents((prev) => prev.map((a, i) => (i === index ? updated : a)));
  }

  function handleAgentRemove(index) {
    setAgents((prev) => prev.filter((_, i) => i !== index));
  }

  function addAgent() {
    setAgents((prev) => [...prev, { ...EMPTY_AGENT }]);
  }

  function validate() {
    const errors = {};
    if (!form.phone.trim()) errors.phone = "Phone is required";
    if (!form.name.trim()) errors.name = "Name is required";
    if (!form.user_type) errors.user_type = "User type is required";
    if (form.age && isNaN(Number(form.age))) errors.age = "Age must be a number";
    for (const a of agents) {
      if (!a.cadre.trim() || !a.kb.trim() || !a.approach.trim()) {
        errors.agents = "All agent fields (Cadre, KB, Approach) are required";
        break;
      }
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!validate()) return;

    setSubmitting(true);

    try {
      if (isEdit) {
        const payload = {
          version,
          phone: form.phone.trim(),
          name: form.name,
          age: form.age ? Number(form.age) : undefined,
          gender: form.gender || undefined,
          state: form.state || undefined,
          district: form.district || undefined,
          sector: form.sector || undefined,
          block: form.block || undefined,
          dob: form.dob || undefined,
          unique_id: form.unique_id || undefined,
          user_type: form.user_type,
        };
        payload.agents = agents;
        const res = await updateAdminUser(userId, payload);
        if (res?.status === 200) {
          setSuccess("User updated successfully.");
          setVersion((v) => v + 1);
        } else if (res?.status === 409) {
          const detail = formatApiError(res?.data?.detail) || res?.data?.message || "";
          setError(
            detail.toLowerCase().includes("phone")
              ? "A user with this phone number already exists."
              : "This user was edited by someone else since you loaded it. Please go back, refresh the list, and try again."
          );
        } else {
          setError(formatApiError(res?.data?.detail) || res?.data?.message || "Update failed. Please try again.");
        }
      } else {
        const payload = {
          phone: form.phone.trim(),
          name: form.name.trim(),
          age: form.age ? Number(form.age) : undefined,
          gender: form.gender || undefined,
          state: form.state || undefined,
          district: form.district || undefined,
          sector: form.sector || undefined,
          block: form.block || undefined,
          dob: form.dob || undefined,
          unique_id: form.unique_id || "na",
          user_type: form.user_type,
        };
        payload.agents = agents;
        const idempotencyKey = uuidv4();
        const res = await createAdminUser(payload, idempotencyKey);
        if (res?.status === 201 || res?.status === 200) {
          setSuccess("User created successfully.");
          setForm(EMPTY_FORM);
          setAgents([{ ...EMPTY_AGENT }]);
        } else if (res?.status === 409) {
          setError("A user with this phone number already exists.");
        } else {
          setError(formatApiError(res?.data?.detail) || res?.data?.message || "Creation failed. Please try again.");
        }
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto w-full">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-6">
        <button
          onClick={() => navigate("/admin/users")}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Users
        </button>
        <span className="text-gray-300">/</span>
        <h1 className="text-lg sm:text-xl font-bold text-gray-900 min-w-0 break-words">
          {isEdit ? `Edit User${existingUser?.name ? ` — ${existingUser.name}` : ""}` : "Create User"}
        </h1>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-5 flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {error}
        </div>
      )}
      {success && (
        <div className="mb-5 flex items-start gap-3 p-4 rounded-xl bg-green-50 border border-green-200 text-sm text-green-700">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        {/* Basic Details Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-5">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">
            Basic Details
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <FormField label="Phone" required>
                <input
                  type="text"
                  required
                  placeholder="+91XXXXXXXXXX"
                  value={form.phone}
                  onChange={(e) => updateForm("phone", e.target.value)}
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                    fieldErrors.phone ? "border-red-400 bg-red-50" : "border-gray-300"
                  }`}
                />
                {fieldErrors.phone && (
                  <p className="text-xs text-red-500 mt-1">{fieldErrors.phone}</p>
                )}
              </FormField>
            </div>

            <div className="sm:col-span-2">
              <FormField label="Name" required>
                <input
                  type="text"
                  required
                  placeholder="Full name"
                  value={form.name}
                  onChange={(e) => updateForm("name", e.target.value)}
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                    fieldErrors.name ? "border-red-400 bg-red-50" : "border-gray-300"
                  }`}
                />
                {fieldErrors.name && (
                  <p className="text-xs text-red-500 mt-1">{fieldErrors.name}</p>
                )}
              </FormField>
            </div>

            <FormField label="Age">
              <input
                type="number"
                min="0"
                max="120"
                placeholder="Age"
                value={form.age}
                onChange={(e) => updateForm("age", e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                  fieldErrors.age ? "border-red-400 bg-red-50" : "border-gray-300"
                }`}
              />
              {fieldErrors.age && (
                <p className="text-xs text-red-500 mt-1">{fieldErrors.age}</p>
              )}
            </FormField>

            <FormField label="Gender">
              <select
                value={form.gender}
                onChange={(e) => updateForm("gender", e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
              >
                <option value="">Select gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </FormField>

            <FormField label="Date of Birth">
              <input
                type="date"
                value={form.dob}
                onChange={(e) => updateForm("dob", e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </FormField>

            <FormField label="Unique ID">
              <input
                type="text"
                placeholder="e.g. national ID (optional)"
                value={form.unique_id}
                onChange={(e) => updateForm("unique_id", e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </FormField>

            <FormField label="State">
              <input
                type="text"
                placeholder="State"
                value={form.state}
                onChange={(e) => updateForm("state", e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </FormField>

            <FormField label="District">
              <input
                type="text"
                placeholder="District"
                value={form.district}
                onChange={(e) => updateForm("district", e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </FormField>

            <FormField label="Sector">
              <input
                type="text"
                placeholder="Sector"
                value={form.sector}
                onChange={(e) => updateForm("sector", e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </FormField>

            <FormField label="Block">
              <input
                type="text"
                placeholder="Block"
                value={form.block}
                onChange={(e) => updateForm("block", e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </FormField>

            <div className="sm:col-span-2">
              <FormField label="User Type" required>
                <select
                  value={form.user_type}
                  onChange={(e) => updateForm("user_type", e.target.value)}
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white ${
                    fieldErrors.user_type ? "border-red-400 bg-red-50" : "border-gray-300"
                  }`}
                >
                  {USER_TYPE_OPTIONS.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
                {fieldErrors.user_type && (
                  <p className="text-xs text-red-500 mt-1">{fieldErrors.user_type}</p>
                )}
              </FormField>
            </div>
          </div>
        </div>

        {/* Agents Card — shown for all user types; multiple only for Internal */}
        {form.user_type && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <div>
                <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
                  Agent
                </h2>
                {!isInternal && (
                  <p className="text-xs text-gray-500 mt-0.5">External users can have exactly one agent.</p>
                )}
              </div>
              {isInternal && (
                <button
                  type="button"
                  onClick={addAgent}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Agent
                </button>
              )}
            </div>

            {fieldErrors.agents && (
              <p className="text-xs text-red-500 mb-3">{fieldErrors.agents}</p>
            )}

            <div className="space-y-4">
              {agents.map((agent, i) => (
                <AgentRow
                  key={i}
                  agent={agent}
                  index={i}
                  onChange={handleAgentChange}
                  onRemove={handleAgentRemove}
                  canRemove={isInternal && agents.length > 1}
                />
              ))}
            </div>
          </div>
        )}

        {/* Version notice in edit mode */}
        {isEdit && (
          <div className="mb-5 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
            Current version: <span className="font-mono font-semibold">v{version}</span>. If another admin edits this user before you save, you will get a conflict error.
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-5 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                {isEdit ? "Saving…" : "Creating…"}
              </>
            ) : (
              isEdit ? "Save Changes" : "Create User"
            )}
          </button>
          <button
            type="button"
            onClick={() => navigate("/admin/users")}
            className="w-full sm:w-auto px-5 py-2.5 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
